import { useEffect, useRef, useState } from "react";
import {
  Animated,
  Pressable,
  Text,
  useWindowDimensions,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import type { LucideIcon } from "lucide-react-native";
import { EMBER } from "../../onboarding/theme";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type FactSheetTab<T extends string> = {
  value: T;
  label: string;
  shortLabel?: string;
  icon: LucideIcon;
};

export default function FactSheetTabControl<T extends string>({
  active,
  onChange,
  tabs,
  style,
}: {
  active: T;
  onChange: (tab: T) => void;
  tabs: FactSheetTab<T>[];
  style?: StyleProp<ViewStyle>;
}) {
  const capturePress = usePressCapture("FactSheetTabControl");
  const { width } = useWindowDimensions();
  const compactTabs = width < 420;
  const [controlWidth, setControlWidth] = useState(0);
  const activeIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.value === active),
  );
  const indicatorProgress = useRef(new Animated.Value(activeIndex)).current;
  const controlPadding = 3;
  const tabGap = 4;
  const indicatorWidth =
    tabs.length > 0
      ? (controlWidth - controlPadding * 2 - tabGap * (tabs.length - 1)) /
        tabs.length
      : 0;

  useEffect(() => {
    Animated.timing(indicatorProgress, {
      toValue: activeIndex,
      duration: 190,
      useNativeDriver: true,
    }).start();
  }, [activeIndex, indicatorProgress]);

  const indicatorTranslateX =
    tabs.length > 1 && indicatorWidth > 0
      ? indicatorProgress.interpolate({
          inputRange: tabs.map((_, index) => index),
          outputRange: tabs.map(
            (_, index) => index * (indicatorWidth + tabGap),
          ),
          extrapolate: "clamp",
        })
      : 0;

  return (
    <View
      onLayout={(event) => setControlWidth(event.nativeEvent.layout.width)}
      style={[
        {
          flexDirection: "row",
          gap: tabGap,
          borderRadius: 999,
          backgroundColor: "rgba(42,37,34,0.06)",
          padding: controlPadding,
          marginTop: 16,
          position: "relative",
          overflow: "hidden",
        },
        style,
      ]}
    >
      {indicatorWidth > 0 && (
        <Animated.View
          pointerEvents="none"
          style={{
            position: "absolute",
            top: controlPadding,
            bottom: controlPadding,
            left: controlPadding,
            width: indicatorWidth,
            borderRadius: 999,
            backgroundColor: "rgba(255,255,255,0.78)",
            borderWidth: 1,
            borderColor: "rgba(42,37,34,0.08)",
            transform: [{ translateX: indicatorTranslateX }],
          }}
        />
      )}
      {tabs.map((tab) => {
        const selected = active === tab.value;
        const Icon = tab.icon;
        return (
          <Pressable
            key={tab.value}
            onPress={() => {
              capturePress(
                pressLabel(tab.label, tab.shortLabel),
                `fact_sheet_tab_${tab.value}`,
              );
              onChange(tab.value);
            }}
            accessibilityRole="tab"
            accessibilityState={{ selected }}
            style={({ pressed }) => ({
              flex: 1,
              minHeight: 32,
              borderRadius: 999,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
              gap: 3,
              paddingHorizontal: 2,
              backgroundColor: pressed
                ? "rgba(255,255,255,0.35)"
                : "transparent",
              zIndex: 1,
            })}
          >
            <Icon
              size={12}
              color={selected ? EMBER.colors.text : EMBER.colors.textLight}
              strokeWidth={1.8}
            />
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              adjustsFontSizeToFit
              style={{
                fontSize: 11,
                lineHeight: 14,
                fontWeight: selected ? "800" : "700",
                color: selected ? EMBER.colors.text : EMBER.colors.textLight,
              }}
            >
              {compactTabs ? (tab.shortLabel ?? tab.label) : tab.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}
